import {
  toWSDateString,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSPill,
  WSSidebar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BillingFeeItemType,
  Currency,
  LineItemType,
  PayableStatus,
  PayeeReviewStatus
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import sortBy from "lodash/sortBy";
import React, { useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Card } from "../../../modules/PaymentApp/components/Card/Card";
import { queriesDeductions, queriesPayableRows, queriesPayables } from "@query";
import { useSession } from "@hooks/useSession";
import { useXWingspanPayerId } from "../../../modules/Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { selectorIsPayableCreator } from "../../components/PayableDetailsHeader/selectorIsPayableCreator";
import { selectorIsLineItemPricedPerUnit } from "../../selectors/selectorIsLineItemPricedPerUnit";
import { useCurrentAccountName } from "../../../v3/providers/CurrentAccountNameProvider";
import { selectorCounterpartyDisplayName } from "../../../shared/selectors/selectorCounterpartyDisplayName";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { WSQueries } from "../../../query/WSQuery";
import { getParentPath } from "../../../utils/goToParentRoute";
import { BillingAttachmentDownloadLink } from "../../components/BillingAttachmentDownloadLink";
import { PayableActivity } from "../../components/PayableActivity";
import { PayableDetailsHeader } from "../../components/PayableDetailsHeader";
import { PayableParentInvoice } from "../../components/PayableParentInvoice";
import { PayableWorkLog } from "../../components/PayableWorkLog";
import { CancelledPayableWorkLogInfo } from "../../../modules/WorkLogs/components/CancelledPayableWorkLogInfo";
import { paymentMethodLabel } from "../../components/InvoiceDetails";
import { QuickbooksHistory } from "../../components/QuickbooksHistory/QuickbooksHistory";
import { QuickbooksWrapper } from "../../components/QuickbooksWrapper/QuickbooksWrapper";
import { Totals, TotalsSection } from "../../components/Totals/Totals";
import { PAYABLE_EXPAND } from "../../constants";
import styles from "../InvoicesInvoiceDetails.module.scss";

type Props = RouteComponentProps<
  { payableId: string; year: string },
  {},
  { backPath?: string }
>;

export const PayableDetails: React.FC<Props> = ({
  match,
  history,
  location
}) => {
  const backPath = location.state?.backPath || getParentPath(history);
  const onBack = () => {
    if (backPath) {
      history.push({
        pathname: backPath,
        search: location.search,
        state: {
          ...(location.state ?? {})
        }
      });
    }
  };

  return (
    <WSSidebar.Container onClose={onBack}>
      <Inner payableId={match.params.payableId} />
    </WSSidebar.Container>
  );
};

type InnerProps = {
  payableId: string;
};

const Inner: React.FC<InnerProps> = ({ payableId }) => {
  const [detailsVisible, setDetailsVisible] = useState(true);
  const { accountId } = useSession();

  const xWingspanAccount = useXWingspanPayerId();
  const queryPayable = queriesPayables.getPayable.useQuery({
    payableId,
    xWingspanAccount,
    expand: PAYABLE_EXPAND
  });

  // "Bill to" is the payer account this payable belongs to. The v3 payable never
  // inlines the `payer` perspective for a payer caller, so the name is read off
  // the account (replaces the v1 `wsName({ user, member })` derivation).
  const currentAccountName = useCurrentAccountName();

  // The contractor name comes off the payable search row's server-resolved
  // `profile.resolvedName` rather than the `payee` expansion the read inlines,
  // so this detail shows the same name as the payables table it was opened
  // from. The expansion itself stays — the header's menu actions pass the whole
  // `Payee` into the reject/remove modals.
  const queryPayableRow = queriesPayableRows.searchPayableRows.useQuery(
    {
      filter: { id: { eq: payableId } },
      page: { size: 1 },
      xWingspanAccount
    },
    { enabled: !!payableId }
  );

  // Deductions carry no id filter, so the payable's `deductionIds` are matched
  // against the payee's deductions client-side.
  const queryDeductions = queriesDeductions.listDeductions.useQuery(
    { filter: { payeeId: { eq: queryPayable.data?.payeeId } } },
    { enabled: (queryPayable.data?.deductionIds ?? []).length > 0 }
  );

  return (
    <WSQueries queries={{ queryPayable }}>
      {({ queryPayable: { data: payable } }) => {
        const payee = payable.payee;
        const payableRow = queryPayableRow.data?.data?.[0];
        // The row's `profile` is empty on payables whose counterparty never
        // signed up, so the inlined `payee` expansion — already on this read —
        // names them when the row cannot.
        const payeeName =
          (payableRow ? selectorSearchRowName(payableRow) : "") ||
          (payee ? selectorCounterpartyDisplayName(payee) : "");
        const payerName = currentAccountName.resolvedName;
        const currency = (payable.currency || "USD") as Currency;

        const payableLineItems = sortBy(payable.lineItems, lineItem =>
          lineItem.metadata?.bulkImporterItemNumber
            ? Number(lineItem.metadata.bulkImporterItemNumber)
            : undefined
        );

        const amount = payable.amount ?? 0;
        const deductionsTotal = payable.amountDetails?.deductions ?? 0;
        const lateFeeTotal = (payable.fees?.items ?? [])
          .filter(item => item.type === BillingFeeItemType.LATE)
          .reduce((sum, item) => sum + (item.amount ?? 0), 0);

        const paidWithPayment = payable.payments?.find(
          payment => payment.method
        );

        const totalsSections: TotalsSection[] = [
          {
            items: payableLineItems.map(lineItem => [
              <>
                <WSFlexBox.CenterY gap="M">
                  <WSText>{lineItem.description || ""}</WSText>
                  {lineItem.lineItemType === LineItemType.REIMBURSEMENT && (
                    <WSPill text="Reimbursable" />
                  )}
                </WSFlexBox.CenterY>
                {selectorIsLineItemPricedPerUnit(lineItem) && (
                  <WSText.ParagraphSm color="gray500">
                    {lineItem.quantity} {lineItem.unit}(s) @{" "}
                    {formatMoney(lineItem.unitCost ?? 0, currency)}/
                    {lineItem.unit}
                  </WSText.ParagraphSm>
                )}
              </>,
              formatMoney(lineItem.totalCost ?? 0, currency)
            ])
          },
          {
            items: [
              {
                bold: true,
                left: "Total",
                right: formatMoney(amount, currency)
              }
            ]
          }
        ];

        if (deductionsTotal > 0) {
          const deductionIds = payable.deductionIds ?? [];
          // One row per deduction applied to this payable, at the amount taken
          // from it — the per-payable figure lives on the deduction's
          // `application` entries, so the rows sum to the deductions total.
          const deductionItems = (queryDeductions.data?.data ?? [])
            .filter(
              deduction => deduction.id && deductionIds.includes(deduction.id)
            )
            .map(deduction => ({
              left: deduction.name ?? "Deduction",
              right: formatMoney(
                -(
                  deduction.application?.find(
                    application => application.payableId === payable.id
                  )?.amountDeducted ??
                  deduction.amount ??
                  0
                ),
                currency
              )
            }));

          totalsSections.push({
            items: [
              ...(deductionItems.length > 0
                ? deductionItems
                : [
                    {
                      left: "Deductions",
                      right: formatMoney(-deductionsTotal, currency)
                    }
                  ]),
              {
                bold: true,
                left: "Contractor gross income",
                right: formatMoney(amount - deductionsTotal, currency)
              }
            ]
          });
        }

        const isDisputed =
          payable.payeeReviewStatus === PayeeReviewStatus.DISPUTED;
        const metadata = payable.metadata ?? {};
        // Exactly one side raised this payable — the payer, or the contractor
        // whose invoice it mirrors.
        const isPayerCreator = selectorIsPayableCreator({ payable, accountId });

        return (
          <WSSidebar.Layout
            header={`Invoice #${payable.invoiceNumber || " Pending"}`}
          >
            <WSElement className={styles.main}>
              <Card mb="XL">
                <WSText.Heading4 mb="M">{payeeName}</WSText.Heading4>

                {payable.status === PayableStatus.CANCELLED && (
                  <CancelledPayableWorkLogInfo payable={payable} />
                )}

                <PayableDetailsHeader
                  payable={payable}
                  payee={payee}
                  payeeName={payeeName}
                />

                {payable.status === PayableStatus.PENDING ? (
                  <WSAlert
                    theme="warning"
                    icon="alert-circle"
                    title="Not yet eligible for payments"
                    mt="XL"
                  >
                    Your invite was sent. We're still waiting for this person to
                    complete their onboarding requirements before they can
                    receive payments.
                  </WSAlert>
                ) : null}

                {isDisputed && (
                  <WSAlert
                    theme="info"
                    icon="info-circle"
                    title="About disputed invoices"
                    mt="XL"
                  >
                    A contractor has disputed this invoice. Review their reason
                    in "All Activity," then approve, edit, or cancel the
                    invoice. As the payer, you have final approval.
                  </WSAlert>
                )}
              </Card>

              <Card mb="XL">
                <WSFlexBox wrap="nowrap" justify="space-between">
                  <WSButton.Link
                    onClick={() => {
                      setDetailsVisible(!detailsVisible);
                    }}
                    rightIcon={detailsVisible ? "caret-up" : "caret-down"}
                  >
                    {detailsVisible ? "Hide" : "Show"} invoice details
                  </WSButton.Link>
                  <WSText.Heading5>
                    {formatMoney(amount, currency)}
                  </WSText.Heading5>
                </WSFlexBox>
                {detailsVisible && (
                  <WSElement mt="XL">
                    <WSElement mb="XL">
                      <WSText.ParagraphSm mb="XS" color="gray500">
                        Bill to
                      </WSText.ParagraphSm>
                      <WSFlexBox.CenterY mb="XL" gap="M">
                        <WSText inline>{payerName}</WSText>
                        {isPayerCreator && (
                          <WSPill theme="blue" text="Created By" />
                        )}
                      </WSFlexBox.CenterY>

                      <WSText.ParagraphSm mb="XS" color="gray500">
                        From
                      </WSText.ParagraphSm>
                      <WSFlexBox.CenterY gap="M">
                        <WSText inline>{payeeName}</WSText>
                        {!isPayerCreator && (
                          <WSPill theme="blue" text="Created By" />
                        )}
                      </WSFlexBox.CenterY>
                    </WSElement>

                    <WSElement mb="XL">
                      <WSText.ParagraphSm mb="XS" color="gray500">
                        Due date
                        {!!payable.scheduledPaymentDate && " (updated)"}
                      </WSText.ParagraphSm>
                      <WSText>
                        {toWSDateString(
                          payable.scheduledPaymentDate || payable.dueDate,
                          "monthDayYear"
                        )}
                      </WSText>
                    </WSElement>
                    {metadata.purchaseOrderNumber ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          PO Number
                        </WSText.ParagraphSm>
                        <WSText>{metadata.purchaseOrderNumber}</WSText>
                      </WSElement>
                    ) : null}
                    {metadata.projectName ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Project Name
                        </WSText.ParagraphSm>
                        <WSText>{metadata.projectName}</WSText>
                      </WSElement>
                    ) : null}
                    {payable.notes ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Notes
                        </WSText.ParagraphSm>
                        <WSText>{payable.notes}</WSText>
                      </WSElement>
                    ) : null}
                    {lateFeeTotal > 0 ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Late fee
                        </WSText.ParagraphSm>
                        <WSText>{formatMoney(lateFeeTotal, currency)}</WSText>
                      </WSElement>
                    ) : null}

                    {paidWithPayment ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Paid with
                        </WSText.ParagraphSm>
                        <WSText>
                          {paymentMethodLabel(paidWithPayment.method)}
                          {paidWithPayment.sourceMetadata?.institution
                            ? ` • ${paidWithPayment.sourceMetadata.institution}`
                            : ""}
                          {paidWithPayment.sourceMetadata?.mask
                            ? ` (${paidWithPayment.sourceMetadata.mask})`
                            : ""}
                        </WSText>
                      </WSElement>
                    ) : null}

                    {payable.events?.depositConfirmedAt ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Payout initiated date
                        </WSText.ParagraphSm>
                        <WSText>
                          {toWSDateString(
                            payable.events.depositConfirmedAt,
                            "monthDayYear"
                          )}
                        </WSText>
                      </WSElement>
                    ) : null}

                    <WSElement mb="XL">
                      <PayableWorkLog payable={payable} />
                    </WSElement>

                    {(payable.attachments?.data ?? []).length > 0 ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Attachments
                        </WSText.ParagraphSm>
                        {(payable.attachments?.data ?? []).map(attachment =>
                          attachment.memberFileId ? (
                            <BillingAttachmentDownloadLink
                              key={attachment.id}
                              fileId={attachment.memberFileId}
                              mb="M"
                            />
                          ) : null
                        )}
                      </WSElement>
                    ) : null}

                    <Totals sections={totalsSections} />
                  </WSElement>
                )}

                {payable.parentInvoiceId && (
                  <WSElement mt="2XL">
                    <PayableParentInvoice
                      parentInvoiceId={payable.parentInvoiceId}
                    />
                  </WSElement>
                )}
              </Card>

              <Card>
                <WSText.Heading5 mb="XL">All activity</WSText.Heading5>
                <PayableActivity payable={payable} />
              </Card>
              <QuickbooksWrapper>
                <QuickbooksHistory
                  Wrapper={Card}
                  mt="XL"
                  entityId={payable.id!}
                />
              </QuickbooksWrapper>
            </WSElement>
          </WSSidebar.Layout>
        );
      }}
    </WSQueries>
  );
};
